import { PanFormValues } from "./panFormFields";

/**
 * The applicant details the Affidavit and DSA / Employer certificates print,
 * derived from the PAN form the operator filled and saved (`panforms.formData`).
 *
 * The PAN form is the reviewed source of truth for these values — the same
 * strings are mirrored onto the PAN application by the API when the form is
 * generated (see pan-form.service.ts `applicantFieldsFromFormData`), so this
 * helper and that one MUST stay in step: change the address order here, change
 * it there.
 *
 * Every field is optional. A box the operator left empty is simply absent, so
 * the caller can fall back to `acknowledgement_*` and then to the application's
 * own fields without an empty string overwriting good data.
 */
export type CertificateApplicantValues = {
  applicantName?: string;
  fatherName?: string;
  address?: string;
  /**
   * The affidavit's address: address lines 1 and 2 + district only — no
   * post office / area rows, no state, no pincode. Mirrors `affidavitAddress`
   * in the API's affidavit-certificate.service (the XLSX export).
   */
  affidavitAddress?: string;
  /**
   * The office address exactly as the PAN form holds it — the Employer
   * Certificate's "Office Address". DSA: the agent's counter off their profile;
   * PCO: the fixed head office under its firm. NEW form: the `office*` block;
   * correction: the one block, when it is ticked Office.
   */
  officeAddress?: string;
  /** DD/MM/YYYY — `formData.dob` is stored as 8 raw digits. */
  dob?: string;
  place?: string;
  /** DD/MM/YYYY, the date typed on the form itself. */
  date?: string;
};

const clean = (value: any) => (typeof value === "string" ? value.trim() : "");

const join = (...parts: any[]) => parts.map(clean).filter(Boolean).join(" ");

/** `13082001` -> `13/08/2001`. Anything else is returned as-is. */
export const formatPanFormDob = (dob: any) => {
  const digits = clean(dob).replace(/\D/g, "");
  if (digits.length !== 8) return clean(dob);
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

export const panFormToCertificate = (
  formData?: Partial<PanFormValues> | null,
  /** The saved PanForm record's own `applicationType` — "NEW" or "CORRECTION". */
  applicationType?: string
): CertificateApplicantValues => {
  if (!formData || typeof formData !== "object") return {};

  const applicantName =
    join(formData.firstName, formData.middleName, formData.lastName) ||
    clean(formData.aadhaarName);

  const fatherName = join(
    formData.fatherFirst,
    formData.fatherMiddle,
    formData.fatherLast
  );

  /*
    The correction form has ONE address block, and its Residence / Office tick
    says what is in it. DSA and PCO corrections open on Office — the counter
    the application is filed at, not where the applicant lives — so that block
    is not an address any certificate may print. Left out, so the caller falls
    back to the `acknowledgement_address` typed at login, which IS the
    applicant's own.

    The NEW form is unaffected: its main block is always residential (an office
    address lives in the separate `office*` fields), even though `addressType`
    is set on it too.
  */
  /*
    Keyed on "is it ticked RESIDENCE" rather than "is it ticked OFFICE": a
    correction saved before the tick existed carries no `addressType` at all,
    and those are DSA / PCO counter addresses too — read as a residence they
    print the counter's address on the affidavit, which is the whole bug this
    guard exists for. Only an explicit RESIDENCE tick makes the block the
    applicant's own.
  */
  const isOfficeAddressBlock =
    applicationType === "CORRECTION" && formData.addressType !== "RESIDENCE";

  // Same row order the PAN form prints the address in.
  const address = isOfficeAddressBlock
    ? ""
    : [
        formData.flat,
        formData.road,
        formData.postOffice,
        formData.area,
        formData.district,
        formData.state,
        formData.pincode,
      ]
        .map(clean)
        .filter(Boolean)
        .join(", ");

  const affidavitAddress = isOfficeAddressBlock
    ? ""
    : [formData.flat, formData.road, formData.district]
        .map(clean)
        .filter(Boolean)
        .join(", ");

  // Same row order the PAN form prints the office block in.
  const officeAddress = (
    applicationType === "CORRECTION"
      ? isOfficeAddressBlock
        ? [
            formData.flat,
            formData.road,
            formData.postOffice,
            formData.area,
            formData.district,
            formData.state,
            formData.pincode,
          ]
        : []
      : [
          formData.officeFlat,
          formData.officeRoad,
          formData.officePostOffice,
          formData.officeArea,
          formData.officeDistrict,
          formData.officeState,
          formData.officePincode,
        ]
  )
    .map(clean)
    .filter(Boolean)
    .join(", ");

  const dob = formatPanFormDob(formData.dob);
  const place = clean(formData.place);
  const date = clean(formData.date);

  return {
    ...(applicantName ? { applicantName } : {}),
    ...(fatherName ? { fatherName } : {}),
    ...(address ? { address } : {}),
    ...(affidavitAddress ? { affidavitAddress } : {}),
    ...(officeAddress ? { officeAddress } : {}),
    ...(dob ? { dob } : {}),
    ...(place ? { place } : {}),
    ...(date ? { date } : {}),
  };
};

export default panFormToCertificate;
